<template>

  <div class="printable-content simple-paper">
    <!-- header informations -->
    <div class="grid grid-cols-2">
      <!-- customer informations -->
      <div>
        <!-- change order ID -->
        <div class="mb-7">
          <h2 class="text-slate-500 inline-block">Change Order #</h2>
          <h2 class="font-bold inline-block">{{ changeOrder.contractId }}</h2>
        </div>

        <!-- customer informations -->
        <div class="mb-5">
          <span class="block mb-1 font-bold">{{ customer.name }}</span>
          <span class="block mb-1">{{ customer.email }}</span>
          <span
            v-if="customer.phone"
            class="block mb-1"
          >{{ customer.phone }}</span>
        </div>
      </div>

      <!-- log and company informations -->
      <div>
        <!-- logo, address -->
        <div class="flex justify-end mb-7">
          <img
            :src="logo"
            alt="Uttrly"
          />
        </div>
        <div class="text-right">
          <span class="block mb-2">11810 Grand Park Ave, Suite 500</span>
          <span class="block mb-2">North Bethesda, MD 20852</span>
          <span class="block">202.681.0825</span>
        </div>
      </div>
    </div>

    <!-- change order date and valid until -->
    <div class="flex gap-4">
      <span>
        <b>Change Order date:</b> {{ getLocalDate(changeOrder.contractDate) }}
      </span>
    </div>

    <!-- change order items details -->
     <div class="pt-6 mt-6">
        <div>
          <h3 class="text-xl font-medium leading-10 tracking-tight">Details</h3>
        </div>

        <!-- change order contract items -->
        <div class="mt-3">
          <div
            v-for="(lineItems, category) in getChangeOrderItems"
            :key="category"
            class="mb-4"
          >
            <span class="font-semibold text-xl mb-2">{{ category }}</span>

            <div
              v-for="(lineItem, idx) in lineItems"
              :key="lineItem.id"
              :class="[
                idx == lineItems.length - 1 ? 'py-4' : 'border-b border-gray-200 py-4'
              ]"
            >
              <div>
                <div class="grid grid-cols-5 gap-4">
                  <div class="col-span-4 flex items-start">
                    <div>
                      <div class="flex items-center">
                        <span class="font-bold">{{ lineItem.title }}</span>
                      </div>

                      <div class="text-justify" v-html="lineItem.scopeOfWork" />
                    </div>
                  </div>

                  <!-- line item total -->
                  <div class="flex flex-col gap-1 text-sm text-right">
                    <span class="font-semibold">{{ currency(lineItem.totalPrice) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- change order total price -->
        <div class="mt-6">
          <div class="grid grid-cols-5 gap-4 border-t-2 border-gray-200 py-4">
            <div class="col-span-5 flex text-right justify-end">
              <div class="mr-3">
                <div>
                  <b>Total:</b>
                </div>
                <div>
                  <b>Previous Contract Amount:</b>
                </div>
                <div>
                  <b>Revised Contract Total:</b>
                </div>
              </div>
              <div>
                <div>
                  {{ currency(changeOrderTotalPrice) }}
                </div>
                <div>
                  {{ currency(previousContractAmount) }}
                </div>
                <div>
                  {{ currency(changeOrderTotalPrice + previousContractAmount) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    <!-- terms and conditions -->
    <div class="mt-6">
      <div class="text-justify" v-html="changeOrder.termsConditions" />
    </div>

  </div>
</template>

<script lang='ts' setup>
import { Budget, BudgetItem } from '@/models/budget'
import { Customer } from '@/models/customer'
import { Project } from '@/models/project'
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import { filter, contains } from 'underscore'
import logo from '@/assets/images/logo.png'
import { computed, PropType } from 'vue'
import { currency, sortLineItemsByCategoryName, toNumber } from '@/utils'

const props = defineProps({
  changeOrder: {
    type: Object as PropType<Proposal>,
    required: true
  },
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  customer: {
    type: Object as PropType<Customer>,
    required: true
  },
  budget: {
    type: Object as PropType<Budget>,
    required: true
  },
  proposal: {
    type: Object as PropType<Proposal>,
    required: true
  },
  changeOrders: {
    type: Array as PropType<Proposal[]>,
    required: true
  }
})


const getLocalDate = computed(() => (date: Date) => {
  return new Date(date).toLocaleDateString()
})

const getChangeOrderItems = computed(() => {
  const items = filter(props.budget.lineItems, (item) => contains(props.changeOrder.lineItems, item.id) && item.selected)
  const sortedItems = sortLineItemsByCategoryName(items)

  return sortedItems
})

const changeOrderTotalPrice = computed(() => {
  return props.budget.lineItems
    .filter(item => props.changeOrder.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})

const previousContractAmount = computed(() => {
  let total = 0
  props.budget.lineItems.forEach((item: BudgetItem) => {
    if (props.proposal.lineItems.includes(item.id) && item.selected) {
      total += toNumber(item.totalPrice)
    }
  })

  // other accepted change orders
  props.changeOrders.forEach((changeOrder) => {
    if (changeOrder.status === PROPOSAL_STATUS.ACCEPTED) {
      props.budget.lineItems.forEach((item: BudgetItem) => {
        if (changeOrder.lineItems.includes(item.id) && item.selected) {
          total += toNumber(item.totalPrice)
        }
      })
    }
  })
  return total
})

</script>

<style lang="scss">
.printable-content {
  width: 200mm !important; /* Largeur A4 */
  padding: 5mm !important;
  background: white;
}

.simple-paper {
  border: 0.7px solid #ccc;
  padding: 20px;
}
</style>
